import { FlexColumn, FlexRow, LoaderBar } from 'components';
import { GetTempleteDetails } from 'hooks';
import { InspectionSectionList } from 'page-components';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown';
import styled from 'styled-components';
import {
    AuthService,
    ControlType,
    TemplateQuestionResponseType,
    TemplateResponseType,
    getParentChildQuestionScore,
    applyParentChildResponseScores,
    getQuestionScoreFallback,
    hasParentChildAnswerSelection,
    getQuestionDisplayScore,
    applyUniformRadioToSection,
    clearSectionAllAllow,
    isUniformRadioAnswer,
    readUniformRadioDisplayText,
    shouldClearSectionAllAllow,
    applyLiveFormulas,
} from 'utility';
import { TranslationServiceInstance as TranslationService } from 'utility/services/translation';
import {
    SPECIAL_FINISH_MAT_TEMPLATE_ID,
    FINISH_MAT_GENERAL_INFO_SECTION_ID,
    FINISH_MAT_GENERAL_INFO_SECTION_NAME,
    FINISH_MAT_SECTION_LAYOUT,
    buildFinishMatOrderedRenderGroups,
} from 'page-components/finish-mat/finish-mat-inspection-utils';

const RECEIVING_CARPET_TEMPLATE_ID = 171370
const RECEIVING_CARPET_AUDIT_TEMPLATE_GUID =
    'cf5c699c-2663-4fec-9e4a-6e513ba9ba9f'
const FREEZER_SAMPLES_TEMPLATE_ID = 172497
const FREEZER_SAMPLES_AUDIT_TEMPLATE_GUID =
    '56de7859-474d-4d48-ad1a-ef2573ff566a'
const FREEZER_SAMPLES_BAG_INFO_QUESTION_ORIGINAL_ID =
    'b3020dba-c77e-47ca-b679-bef0ef6fdc34'
const RECEIVING_CARPET_DETAILS_ROLL_QUESTION_ORIGINAL_ID =
    'dd7fed67-bde5-4292-846a-464bbbf2fe76'

const normalizeSectionName = name => (name || '').trim().toLowerCase()

const isNamedSection = (name, baseName) => {
    const sectionName = normalizeSectionName(name)
    const base = normalizeSectionName(baseName)
    if (!sectionName || !base) return false
    if (sectionName === base) return true
    const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(`^${escaped}\\s*\\(\\d+\\)\\s*$`).test(sectionName)
}

const sectionHasQuestionOriginalId = (section, questionOriginalId) =>
    (section?.Questions || []).some(question => {
        const questionId = String(
            question?.QuestionOriginalId || question?.Id || '',
        ).toLowerCase()
        return questionId === questionOriginalId
    })

const isFreezerSamplesBagInfoSection = section =>
    isNamedSection(section?.SectionName, 'Bag Info') ||
    sectionHasQuestionOriginalId(
        section,
        FREEZER_SAMPLES_BAG_INFO_QUESTION_ORIGINAL_ID,
    )

const isReceivingCarpetDetailsSection = section => {
    if (!section) return false
    if (isNamedSection(section.SectionName, 'Details')) return true
    return sectionHasQuestionOriginalId(
        section,
        RECEIVING_CARPET_DETAILS_ROLL_QUESTION_ORIGINAL_ID,
    )
}

const LanguageSelectorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 16px;
    width: 100%;

    .language-label {
        font-weight: 500;
        color: ${({ theme }) => theme.colors.black || '#333'};
    }

    .language-dropdown {
        min-width: 150px;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        width: 100%;

        .language-dropdown {
            min-width: unset;
            width: 100%;
        }
    }
`

const extractShowSectionDropdownFlag = source => {
    if (!source || typeof source !== 'object') {
        return undefined
    }

    const possibleKeys = [
        'ShowSectionAsDropdown',
        'ShowSectionAsDropDown',
        'ShowSectionsAsDropdown',
        'ShowSectionsAsDropDown',
        'ShowSectionDropdown',
        'ShowSectionDropDown',
        'showSectionAsDropdown',
        'showSectionAsDropDown',
        'showSectionDropdown',
        'showSectionDropDown',
    ]

    const matchedKey = possibleKeys.find(key =>
        Object.prototype.hasOwnProperty.call(source, key),
    )

    if (!matchedKey) {
        return undefined
    }

    const value = source[matchedKey]

    if (value === undefined || value === null) {
        return undefined
    }

    if (typeof value === 'string') {
        const lowered = value.toLowerCase()
        if (lowered === 'true') {
            return true
        }
        if (lowered === 'false') {
            return false
        }
    }

    if (typeof value === 'number') {
        if (value === 1) {
            return true
        }
        if (value === 0) {
            return false
        }
    }

    return Boolean(value)
}

const normalizeBooleanFlag = value => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const lowered = value.trim().toLowerCase()
        return lowered === 'true' || lowered === '1'
    }
    return false
}

const isQuestionAnswered = question => {
    if (!question) {
        return false
    }

    const answers = question.Answers || {}

    const hasOtherResponse = () => {
        if (!question.IsOtherAnswerSelected) {
            return false
        }

        if (!Array.isArray(answers.OtherAnswerResponse)) {
            return false
        }

        return answers.OtherAnswerResponse.some(other => {
            if (!other) {
                return false
            }

            const value =
                typeof other.OtherAnswer === 'string'
                    ? other.OtherAnswer.trim()
                    : other.OtherAnswer

            return value !== undefined && value !== null && value !== ''
        })
    }

    const hasDropdownOther = () => {
        if (!Array.isArray(question.dropdownValue)) {
            return false
        }

        if (!question.dropdownValue.length) {
            return false
        }

        const normalIds = (answers.MultipleResponse || []).map(item =>
            String(item.Id),
        )
        const firstValue = question.dropdownValue[0]

        if (firstValue === undefined || firstValue === null) {
            return false
        }

        return !normalIds.includes(String(firstValue))
    }

    const hasParentChildSelection = () =>
        hasParentChildAnswerSelection(
            answers.ParentChildResponse,
            answers,
        )

    if (
        question.TypeOfResponse === TemplateResponseType.Multiple ||
        question.MultiResponseType === TemplateQuestionResponseType.Multiple ||
        question.MultiResponseType === TemplateQuestionResponseType.Single
    ) {
        const selectedCount = (answers.MultipleResponse || []).filter(
            option => option?.IsSelected,
        ).length

        return (
            selectedCount > 0 ||
            hasOtherResponse() ||
            hasDropdownOther()
        )
    }

    if (question.QuestionResponseId === ControlType.Media) {
        return Array.isArray(question.Files) && question.Files.length > 0
    }

    if (hasParentChildSelection()) {
        return true
    }

    if (
        question.TypeOfResponse === TemplateResponseType.Single &&
        answers.SingleResponse
    ) {
        const response = answers.SingleResponse.Response
        return (
            response !== undefined &&
            response !== null &&
            response !== ''
        )
    }

    if (answers.SingleResponse && answers.SingleResponse.Response !== undefined) {
        const response = answers.SingleResponse.Response
        return (
            response !== undefined &&
            response !== null &&
            response !== ''
        )
    }

    return false
}

export const InspectionPreviewScreen = () => {
    const params = useParams()
    const navigate = useNavigate()

    const resolvedTemplateId = params?.id
    const { data: serverData, loading: templateLoading, error: publicError } = GetTempleteDetails(resolvedTemplateId)

    const [data, setData] = useState(null)
    const [changed, setChanged] = useState(0)

    useEffect(() => {
        if (serverData && !data) {
            setData(JSON.parse(JSON.stringify(serverData)))
        }
    }, [serverData, data])

    const languageOptions = [
        { key: 'en', text: 'English (primary)', value: 'English' },
        { key: 'es', text: 'Spanish', value: 'Spanish' },
        { key: 'ro', text: 'Romanian', value: 'Romanian' },
    ]
    const [selectedLanguage, setSelectedLanguage] = useState('English')
    const [translating, setTranslating] = useState(false)
    const [translatedData, setTranslatedData] = useState(null)
    const initialTemplateRef = useRef(null)

    const user = AuthService.getUser()
    const companyId = user?.Companies?.[0]?.CompanyId ?? user?.CompanyId
    const isSpecialFinishMatTemplate =
        Number(companyId) === 34930 &&
        (Number(data?.OriginalTemplateId) === SPECIAL_FINISH_MAT_TEMPLATE_ID ||
            Number(data?.TemplateId) === SPECIAL_FINISH_MAT_TEMPLATE_ID ||
            Number(data?.Id) === SPECIAL_FINISH_MAT_TEMPLATE_ID)
    const isLanguageFeatureEnabled =
        normalizeBooleanFlag(user?.IsMultipleLanguageAllowed) ||
        normalizeBooleanFlag(user?.Companies?.[0]?.IsMultipleLanguageAllowed) ||
        companyId === 34930 ||
        companyId === 60551
    const getPreviewSectionCopyButtonLabel = useCallback(
        section => {
            if (Number(companyId) !== 34930) return 'Copy'

            const numericIds = [
                Number(data?.OriginalTemplateId),
                Number(data?.TemplateId),
                Number(data?.Id),
            ]
            const guids = [
                String(data?.AuditTemplateId || '').toLowerCase(),
                String(data?.UniqueId || '').toLowerCase(),
                String(data?.TemplateUniqueId || '').toLowerCase(),
                String(params?.id || '').toLowerCase(),
            ]
            const isFreezerSamplesTemplate =
                numericIds.includes(FREEZER_SAMPLES_TEMPLATE_ID) ||
                guids.includes(FREEZER_SAMPLES_AUDIT_TEMPLATE_GUID)
            const isReceivingCarpetTemplate =
                numericIds.includes(RECEIVING_CARPET_TEMPLATE_ID) ||
                guids.includes(RECEIVING_CARPET_AUDIT_TEMPLATE_GUID)

            if (isFreezerSamplesTemplate && isFreezerSamplesBagInfoSection(section)) {
                return null
            }
            if (isReceivingCarpetTemplate) {
                return isReceivingCarpetDetailsSection(section) ? 'Add New' : null
            }
            return 'Add New'
        },
        [companyId, data, params?.id],
    )

    const showSectionDropdown = useMemo(() => {
        if (!data) return false

        const directFlag = extractShowSectionDropdownFlag(data)
        if (typeof directFlag === 'boolean') {
            return directFlag
        }

        const companySettingFlag = Array.isArray(data?.CompanySetting)
            ? data.CompanySetting
                .map(setting => extractShowSectionDropdownFlag(setting))
                .find(value => typeof value === 'boolean')
            : undefined

        if (typeof companySettingFlag === 'boolean') {
            return companySettingFlag
        }

        if (typeof data?.IsSectionVisibleInDropdown === 'boolean') {
            return data.IsSectionVisibleInDropdown
        }

        return false
    }, [data])

    useEffect(() => {
        if (
            isLanguageFeatureEnabled &&
            data &&
            data.Sections &&
            !initialTemplateRef.current
        ) {
            initialTemplateRef.current = JSON.parse(JSON.stringify(data))
        }
    }, [data, isLanguageFeatureEnabled])

    useEffect(() => {
        if (!isLanguageFeatureEnabled) {
            setTranslatedData(null)
            return
        }

        const templateSource = initialTemplateRef.current

        const translateTemplate = async () => {
            if (!templateSource || selectedLanguage === 'English') {
                setTranslatedData(null)
                return
            }

            setTranslating(true)
            try {
                const translated = await TranslationService.translateTemplate(
                    templateSource,
                    selectedLanguage,
                    'English',
                )
                setTranslatedData(translated)
            } catch (error) {
                console.error('Translation error:', error)
                setTranslatedData(null)
            } finally {
                setTranslating(false)
            }
        }

        translateTemplate()
    }, [selectedLanguage, isLanguageFeatureEnabled])

    const displayData = useMemo(() => {
        if (
            !isLanguageFeatureEnabled ||
            selectedLanguage === 'English' ||
            !translatedData ||
            translating
        ) {
            return data
        }
        return translatedData
    }, [data, translatedData, selectedLanguage, translating, isLanguageFeatureEnabled])

    const sectionsForRender = useMemo(() => {
        if (
            !isLanguageFeatureEnabled ||
            selectedLanguage === 'English' ||
            !translatedData ||
            translating ||
            !Array.isArray(data?.Sections)
        ) {
            return data?.Sections || []
        }

        const translatedSections = translatedData.Sections || []

        return data.Sections.map((section, sectionIndex) => {
            const tSection = translatedSections[sectionIndex]
            if (!tSection) {
                return section
            }

            const mergedQuestions = (section.Questions || []).map(
                (question, questionIndex) => {
                    const tQuestion = tSection.Questions?.[questionIndex]
                    if (!tQuestion) {
                        return question
                    }

                    const mergedQuestion = {
                        ...question,
                    }

                    if (tQuestion.Question) mergedQuestion.Question = tQuestion.Question
                    if (tQuestion.FAQ) mergedQuestion.FAQ = tQuestion.FAQ

                    const originalAnswers = question.Answers || {}
                    const translatedAnswers = tQuestion.Answers || {}
                    const translatedTemplateType = tQuestion.TemplateResponseType || {}

                    let mergedAnswers = originalAnswers

                    const normalizeLabel = text => {
                        if (!text || typeof text !== 'string') return text
                        return text.replace(/\(\d+\)\s*$/, '').trim()
                    }

                    if (Array.isArray(originalAnswers.MultipleResponse)) {
                        const translatedAnswerOptions = translatedAnswers.MultipleResponse || []
                        const translatedTemplateOptions = translatedTemplateType.MultipleResponse || []
                        const translatedUserOptions = translatedTemplateType.UserMultipleResponse || []

                        const mappedMultipleResponse = originalAnswers.MultipleResponse.map(opt => {
                            let tOpt = null

                            const idx = originalAnswers.MultipleResponse.indexOf(opt)
                            if (idx > -1 && translatedAnswerOptions[idx]) {
                                tOpt = translatedAnswerOptions[idx]
                            } else {
                                const baseLabel = normalizeLabel(opt.DisplayText)

                                tOpt =
                                    translatedTemplateOptions.find(to =>
                                        baseLabel ? normalizeLabel(to.DisplayText) === baseLabel : false,
                                    ) ||
                                    translatedUserOptions.find(to =>
                                        baseLabel ? normalizeLabel(to.DisplayText) === baseLabel : false,
                                    ) ||
                                    null
                            }

                            if (!tOpt) return opt

                            return {
                                ...opt,
                                DisplayText: tOpt.DisplayText || opt.DisplayText,
                                HelpText: tOpt.HelpText || opt.HelpText,
                            }
                        })

                        mergedAnswers = { ...mergedAnswers, MultipleResponse: mappedMultipleResponse }
                    }

                    if (
                        Array.isArray(originalAnswers.ParentChildResponse) &&
                        Array.isArray(translatedAnswers.ParentChildResponse)
                    ) {
                        const mappedParentChild = originalAnswers.ParentChildResponse.map((parent, pIndex) => {
                            const tParent = translatedAnswers.ParentChildResponse[pIndex]
                            if (!tParent) return parent

                            const mergedParent = { ...parent }

                            if (tParent.ParentName) mergedParent.ParentName = tParent.ParentName
                            if (tParent.ChildLabel) mergedParent.ChildLabel = tParent.ChildLabel

                            if (Array.isArray(parent.Children) && Array.isArray(tParent.Children)) {
                                mergedParent.Children = parent.Children.map((child, cIndex) => {
                                    const tChild = tParent.Children[cIndex]
                                    if (!tChild) return child
                                    return {
                                        ...child,
                                        ChildName: tChild.ChildName || child.ChildName,
                                    }
                                })
                            }
                            return mergedParent
                        })

                        mergedAnswers = { ...mergedAnswers, ParentChildResponse: mappedParentChild }
                    }

                    if (
                        originalAnswers.SingleResponse &&
                        translatedAnswers.SingleResponse &&
                        typeof translatedAnswers.SingleResponse.Response === 'string' &&
                        translatedAnswers.SingleResponse.Response.trim()
                    ) {
                        mergedAnswers = {
                            ...mergedAnswers,
                            SingleResponse: {
                                ...originalAnswers.SingleResponse,
                                Response: translatedAnswers.SingleResponse.Response,
                            },
                        }
                    }

                    if (Array.isArray(question.Notes) && Array.isArray(tQuestion.Notes)) {
                        mergedQuestion.Notes = question.Notes.map((note, noteIndex) => {
                            const tNote = tQuestion.Notes[noteIndex]
                            if (!tNote || !tNote.Notes || typeof tNote.Notes !== 'string') {
                                return note
                            }
                            return { ...note, Notes: tNote.Notes }
                        })
                    }

                    mergedQuestion.Answers = mergedAnswers
                    return mergedQuestion
                },
            )

            return {
                ...section,
                SectionName: tSection.SectionName || section.SectionName,
                SectionURL:
                    tSection.SectionURL ||
                    tSection.SectionUrl ||
                    section.SectionURL ||
                    section.SectionUrl ||
                    section?.Questions?.[0]?.SectionURL ||
                    section?.Questions?.[0]?.SectionUrl ||
                    null,
                Questions: mergedQuestions,
            }
        })
    }, [data, isLanguageFeatureEnabled, selectedLanguage, translatedData, translating])

    const sectionsForRenderForPreview = useMemo(() => {
        // Enforce answers object presence to avoid crashes when rendering raw templates
        return (Array.isArray(sectionsForRender) ? sectionsForRender : []).map(section => ({
            ...section,
            Questions: Array.isArray(section?.Questions)
                ? section.Questions.map(q => {
                    const ans = { ...(q.Answers || {}) }

                    // Initialize MultipleResponse from TemplateResponseType
                    if (!Array.isArray(ans.MultipleResponse)) {
                        const standardOpts = data?.TemplateResponseType?.MultipleResponse || []
                        const userOpts = data?.TemplateResponseType?.UserMultipleResponse || []

                        const optionMatcher = opt =>
                            opt.GroupId === q.QuestionResponseId ||
                            opt.Id === q.QuestionResponseId ||
                            (q.QuestionResponseId === 0 &&
                                (opt.GroupId === q.Id || opt.Id === q.Id))

                        const matchedUserOptions = userOpts.filter(optionMatcher)
                        const matchedStandardOptions = standardOpts.filter(optionMatcher)
                        const baseOptions =
                            matchedUserOptions.length > 0
                                ? matchedUserOptions
                                : matchedStandardOptions

                        const options = baseOptions.map(opt => ({
                            ...opt,
                            IsSelected: false,
                        }))

                        ans.MultipleResponse = options
                    }

                    const inferredMultiResponseType = (() => {
                        if (
                            q.MultiResponseType ===
                                TemplateQuestionResponseType.Single ||
                            q.MultiResponseType ===
                                TemplateQuestionResponseType.Multiple
                        ) {
                            return q.MultiResponseType
                        }

                        if (
                            (q.TypeOfResponse ?? q.QuestionType) ===
                            TemplateResponseType.Multiple
                        ) {
                            const selectedOptionType = ans.MultipleResponse?.find(
                                opt =>
                                    opt?.MultiResponseType ===
                                        TemplateQuestionResponseType.Single ||
                                    opt?.MultiResponseType ===
                                        TemplateQuestionResponseType.Multiple,
                            )?.MultiResponseType

                            return (
                                selectedOptionType ||
                                TemplateQuestionResponseType.Single
                            )
                        }

                        return 0
                    })()

                    // For SingleResponse text areas
                    if (!ans.SingleResponse) {
                        ans.SingleResponse = { Response: '' }
                    }

                    // For ParentChildResponse
                    if (!ans.ParentChildResponse && Array.isArray(q.ParentChildResponse)) {
                        ans.ParentChildResponse = JSON.parse(JSON.stringify(q.ParentChildResponse)).map(parent => ({
                            ...parent,
                            Id: parent.Id || parent.ParentId,
                            IsSelected: false,
                            Children: Array.isArray(parent.Children) ? parent.Children.map(c => ({
                                ...c,
                                Id: c.Id || c.ChildId,
                                ParentId: c.ParentId || parent.ParentId || parent.Id,
                                IsScore: c.IsScore !== undefined ? c.IsScore : !!c.IsChildScore,
                                Score: c.Score !== undefined ? c.Score : (c.ChildScore || 0),
                                IsSelected: false,
                            })) : [],
                        }))
                    }

                    return {
                        ...q,
                        FAQ: q.FAQ || q.Question,
                        TypeOfResponse: typeof q.TypeOfResponse !== 'undefined' ? q.TypeOfResponse : q.QuestionType,
                        MultiResponseType: inferredMultiResponseType,
                        DisplayOrder: typeof q.DisplayOrder !== 'undefined' ? q.DisplayOrder : (q.Order || q.QuestionOrder),
                        QuestionReferences:
                            q.QuestionReferences || q.QuestionReference || [],
                        Answers: ans,
                    }
                })
                : section.Questions,
            SectionURL:
                section?.SectionURL ||
                section?.SectionUrl ||
                section?.Questions?.[0]?.SectionURL ||
                section?.Questions?.[0]?.SectionUrl ||
                null,
        }))
    }, [sectionsForRender, data])

    const finishMatRenderGroups = useMemo(() => {
        if (!isSpecialFinishMatTemplate) {
            return []
        }
        return buildFinishMatOrderedRenderGroups(sectionsForRenderForPreview)
    }, [isSpecialFinishMatTemplate, sectionsForRenderForPreview])

    const finishMatGroupCount = finishMatRenderGroups.length

    const templateTotalQuestions = useMemo(() => {
        let total = 0
        const sections = displayData?.Sections || data?.Sections || []
        if (!Array.isArray(sections)) {
            return total
        }
        sections.forEach(section => {
            if (!Array.isArray(section?.Questions)) return
            total += section.Questions.length
        })
        return total
    }, [displayData?.Sections, data?.Sections])

    const totalScore = useMemo(() => {
        let total = 0
        if (!data?.Sections?.length) return total
        data.Sections.forEach(section => {
            if (!section?.Questions?.length) return
            section.Questions.forEach(question => {
                total += getQuestionDisplayScore(question)
            })
        })
        return total
    }, [data])

    const onAnswerChanged = (sectionId, question, answer) => {
        if (data) {
            const sections = data.Sections.map(section => {
                if (section.SectionId === sectionId) {
                    if (isUniformRadioAnswer(answer)) {
                        const uniformText = readUniformRadioDisplayText(answer);
                        return (
                            (uniformText &&
                                applyUniformRadioToSection(
                                    section,
                                    uniformText,
                                    data?.TemplateResponseType?.MultipleResponse,
                                )) ||
                            section
                        );
                    }
                    const questions = section.Questions.map(item => {
                        if ((item.Id && item.Id?.toString()) === (question.Id && question.Id?.toString())) {
                            // Merge the enriched question fields (TypeOfResponse, MultiResponseType, etc.)
                            // from the passed `question` object into `temp`, because raw data.Sections
                            // questions may only have `QuestionType` (template format), not `TypeOfResponse`.
                            const typeOfResponse = question.TypeOfResponse ?? question.QuestionType ?? item.TypeOfResponse ?? item.QuestionType
                            const multiResponseType = question.MultiResponseType ?? item.MultiResponseType ?? 0
                            let temp = {
                                ...item,
                                TypeOfResponse: typeOfResponse,
                                MultiResponseType: multiResponseType,
                                FAQ: item.FAQ || item.Question,
                                DisplayOrder: item.DisplayOrder ?? item.Order ?? item.QuestionOrder,
                            }
                            if (!temp.Answers) {
                                temp.Answers = {}
                            }
                            // Ensure Answers has the enriched MultipleResponse/ParentChildResponse
                            // from the question passed in (which already has them initialized)
                            if (!Array.isArray(temp.Answers.MultipleResponse) && Array.isArray(question.Answers?.MultipleResponse)) {
                                temp.Answers = { ...temp.Answers, MultipleResponse: question.Answers.MultipleResponse }
                            }
                            if (!temp.Answers.ParentChildResponse && question.Answers?.ParentChildResponse) {
                                temp.Answers = { ...temp.Answers, ParentChildResponse: question.Answers.ParentChildResponse }
                            }

                            if (answer && typeof answer === 'object' && Object.prototype.hasOwnProperty.call(answer, '__ncReply')) {
                                const base = Object.prototype.hasOwnProperty.call(item, 'NCReply')
                                    ? { ...item, NCReply: answer.__ncReply }
                                    : { ...item }

                                if (companyId === 34930) {
                                    const existingCncInAnswers = item.Answers && item.Answers.CNCResponse ? item.Answers.CNCResponse : null
                                    const existingCncRoot = item.CNCResponse || null
                                    const existingCnc = existingCncInAnswers || existingCncRoot || {}
                                    const cncPayload = answer.__cncResponse || {}

                                    const responseData = {
                                        QuestionId: cncPayload.QuestionId || existingCnc.ResponseData?.QuestionId || existingCnc.QuestionId || item.Id,
                                        CNCResponseId: cncPayload.CNCResponseId || existingCnc.ResponseData?.CNCResponseId || 0,
                                        CNCValue: typeof cncPayload.CNCValue === 'number' ? cncPayload.CNCValue : existingCnc.ResponseData?.CNCValue ?? 0,
                                        AdditionalAnswer: cncPayload.AdditionalAnswer ?? existingCnc.ResponseData?.AdditionalAnswer ?? '',
                                    }

                                    const updatedQuestionData = Array.isArray(existingCnc.QuestionData)
                                        ? existingCnc.QuestionData.map(qd => {
                                            if (!qd || !Array.isArray(qd.Options)) return qd
                                            return {
                                                ...qd,
                                                Options: qd.Options.map(opt => {
                                                    if (opt && typeof opt === 'object') {
                                                        return { ...opt, IsSelected: opt.Id === responseData.CNCResponseId }
                                                    }
                                                    return opt
                                                }),
                                            }
                                        })
                                        : existingCnc.QuestionData

                                    const updatedCnc = {
                                        ...existingCnc,
                                        QuestionId: existingCnc.QuestionId || responseData.QuestionId,
                                        ResponseData: responseData,
                                        ...(updatedQuestionData && { QuestionData: updatedQuestionData }),
                                    }

                                    if (existingCncInAnswers) {
                                        temp = {
                                            ...base,
                                            Answers: { ...item.Answers, CNCResponse: updatedCnc },
                                        }
                                    } else {
                                        temp = { ...base, CNCResponse: updatedCnc }
                                    }
                                } else {
                                    temp = base
                                }
                            } else if (typeOfResponse === TemplateResponseType.Multiple) {
                                temp = {
                                    ...temp,
                                    Answers: {
                                        ...temp.Answers,
                                        ...answer,
                                    },
                                }
                                if (temp.Answers.OtherAnswerResponse && Array.isArray(temp.Answers.OtherAnswerResponse) && temp.Answers.OtherAnswerResponse.length > 0) {
                                    temp.Answers.IsOtherAnswerSelected = true
                                } else {
                                    temp.Answers.IsOtherAnswerSelected = false
                                }
                                temp.IsOtherAnswerSelected = temp.Answers.IsOtherAnswerSelected
                            } else if (answer && answer.ParentChildResponse) {
                                temp = {
                                    ...temp,
                                    Answers: {
                                        ...temp.Answers,
                                        ...answer,
                                        ParentChildResponse:
                                            applyParentChildResponseScores(
                                                answer.ParentChildResponse,
                                            ),
                                    },
                                    IsAnswered: true,
                                    IsOtherAnswerSelected:
                                        !!answer.IsOtherAnswerSelected,
                                }
                            } else if (typeOfResponse === TemplateResponseType.Single && temp.Answers.SingleResponse) {
                                if (typeof answer === 'object' && 'Response' in answer && 'IsSelected' in answer) {
                                    temp = {
                                        ...temp,
                                        Answers: {
                                            ...temp.Answers,
                                            SingleResponse: { ...temp.Answers.SingleResponse, Response: answer.Response, IsSelected: answer.IsSelected },
                                        },
                                    }
                                } else {
                                    temp = {
                                        ...temp,
                                        Answers: {
                                            ...temp.Answers,
                                            SingleResponse: { ...temp.Answers.SingleResponse, Response: answer },
                                        },
                                    }
                                }
                            } else {
                                const multipleResponses = (temp.Answers.MultipleResponse || []).map(choice => {
                                    if (multiResponseType === TemplateQuestionResponseType.Multiple) {
                                        return { ...choice, IsSelected: answer?.includes(choice.Id) }
                                    }
                                    choice.IsSelected = false
                                    if (answer.Id === choice.Id) {
                                        return { ...choice, IsSelected: !choice.IsSelected }
                                    }
                                    return choice
                                })
                                temp = {
                                    ...temp,
                                    Answers: { ...temp.Answers, MultipleResponse: multipleResponses },
                                }
                            }

                            if (typeOfResponse === TemplateResponseType.Multiple) {
                                const selectedOption = Array.isArray(temp.Answers.MultipleResponse)
                                    ? temp.Answers.MultipleResponse.find(opt => opt.IsSelected)
                                    : null;

                                let score = 0;
                                if (selectedOption) {
                                    if (typeof selectedOption.Score === 'number') {
                                        score = selectedOption.Score;
                                    } else if (data?.TemplateResponseType?.MultipleResponse) {
                                        const templateOption = data.TemplateResponseType.MultipleResponse.find(
                                            opt =>
                                                (opt.Id === selectedOption.Id ||
                                                    opt.GroupId === selectedOption.QuestionResponseId ||
                                                    opt.Id === selectedOption.QuestionResponseId ||
                                                    (temp.QuestionResponseId && (opt.GroupId === temp.QuestionResponseId || opt.Id === temp.QuestionResponseId))) &&
                                                opt.DisplayText === selectedOption.DisplayText,
                                        );
                                        if (templateOption && typeof templateOption.Score === 'number') {
                                            score = templateOption.Score;
                                            selectedOption.Score = score;
                                        }
                                    }
                                }
                                temp.Score = score;

                                if (Array.isArray(temp.Answers.MultipleResponse) && data?.TemplateResponseType?.MultipleResponse) {
                                    temp.Answers.MultipleResponse = temp.Answers.MultipleResponse.map(mr => {
                                        if (typeof mr.Score !== 'number') {
                                            const templateMatch = data.TemplateResponseType.MultipleResponse.find(
                                                opt =>
                                                    (opt.Id === mr.Id ||
                                                        opt.GroupId === mr.QuestionResponseId ||
                                                        opt.Id === mr.QuestionResponseId ||
                                                        (temp.QuestionResponseId && (opt.GroupId === temp.QuestionResponseId || opt.Id === temp.QuestionResponseId))) &&
                                                    opt.DisplayText === mr.DisplayText,
                                            );
                                            if (templateMatch && typeof templateMatch.Score === 'number') {
                                                return { ...mr, Score: templateMatch.Score };
                                            }
                                        }
                                        return mr;
                                    });
                                }
                            } else if (temp.Answers && temp.Answers.ParentChildResponse && Array.isArray(temp.Answers.ParentChildResponse) && temp.Answers.ParentChildResponse.length > 0) {
                                temp.Score = getParentChildQuestionScore(
                                    temp.Answers.ParentChildResponse,
                                    getQuestionScoreFallback(item),
                                    temp.Answers.OtherAnswerResponse,
                                )
                            } else if (temp.TypeOfResponse === TemplateResponseType.Single) {
                                if (temp.Answers && temp.Answers.SingleResponse && temp.Answers.SingleResponse.Response !== undefined && temp.Answers.SingleResponse.Response !== null && temp.Answers.SingleResponse.Response !== '') {
                                    temp.Score = typeof item.Score === 'number' ? item.Score : 0
                                } else {
                                    temp.Score = 0
                                }
                            }
                            const answered = isQuestionAnswered(temp)
                            temp.IsAnswered = answered
                            if (temp.Answers) temp.Answers.IsAnswered = answered

                            return temp
                        }
                        return item
                    })
                    const nextSection = { ...section, Questions: questions };
                    return shouldClearSectionAllAllow(answer)
                        ? clearSectionAllAllow(nextSection)
                        : nextSection;
                }
                return section
            })

            setData({ ...data, Sections: applyLiveFormulas(sections) })
            setChanged(changed + 1)
        }
    }

    // addNotes is called by InspectionSectionList as: addNotes(item, note)
    // item = the question object (has item.Id and item.SectionId), note = {FAQId, IsInternal, Notes}
    const saveNote = async (item, note) => {
        if (!data || !data.Sections || !item) return

        const QuestionId = item.Id
        const { SectionId } = item;
        const newData = JSON.parse(JSON.stringify(data))

        const sIndex = newData.Sections.findIndex(s => s.SectionId === SectionId)
        if (sIndex > -1) {
            const qIndex = newData.Sections[sIndex].Questions.findIndex(q => q.Id === QuestionId)
            if (qIndex > -1) {
                const targetQ = newData.Sections[sIndex].Questions[qIndex]
                if (!targetQ.Notes) targetQ.Notes = []

                // Upsert: update existing note of same type or push new
                const isInternal = !!note.IsInternal
                const existingIdx = targetQ.Notes.findIndex(n => !!n.IsInternal === isInternal)
                const noteEntry = {
                    Id: existingIdx > -1 ? targetQ.Notes[existingIdx].Id : Date.now(),
                    Notes: note.Notes,
                    IsInternal: isInternal,
                    CreatedOn: new Date().toISOString(),
                }
                if (existingIdx > -1) {
                    targetQ.Notes[existingIdx] = noteEntry
                } else {
                    targetQ.Notes.push(noteEntry)
                }
                setData(newData)
            }
        }
    }

    // addMedia is called by InspectionSectionList as: addMedia(item.Id, files, item.SectionId)
    // files can be a FileList (native File objects from browser) or an array of objects with .local===false for edits
    const addMedia = async (QuestionId, files, SectionId) => {
        if (!data || !data.Sections) return []

        const newData = JSON.parse(JSON.stringify(data))
        const newMediaList = []

        // Find the section by SectionId — also try by index if not found
        let sIndex = newData.Sections.findIndex(s => s.SectionId === SectionId)
        if (sIndex === -1) {
            // Fallback: search all sections for the question
            sIndex = newData.Sections.findIndex(s =>
                Array.isArray(s.Questions) && s.Questions.some(q => q.Id === QuestionId),
            )
        }

        if (sIndex > -1) {
            const qIndex = newData.Sections[sIndex].Questions.findIndex(q => q.Id === QuestionId)
            if (qIndex > -1) {
                const targetQ = newData.Sections[sIndex].Questions[qIndex]
                if (!targetQ.Answers) targetQ.Answers = {}
                if (!targetQ.Answers.MediaResponse) targetQ.Answers.MediaResponse = []

                Array.from(files).forEach((file, idx) => {
                    // Case 1: editing caption/comment on an existing uploaded file
                    if (file.local === false && file.id) {
                        const existingMediaIdx = targetQ.Answers.MediaResponse.findIndex(
                            m => m.MediaId === file.id || m.MediaId === file.MediaId,
                        )
                        if (existingMediaIdx > -1) {
                            targetQ.Answers.MediaResponse[existingMediaIdx] = {
                                ...targetQ.Answers.MediaResponse[existingMediaIdx],
                                Caption: file.Caption ?? targetQ.Answers.MediaResponse[existingMediaIdx].Caption,
                                Comment: file.Comment ?? targetQ.Answers.MediaResponse[existingMediaIdx].Comment,
                            }
                            newMediaList.push(targetQ.Answers.MediaResponse[existingMediaIdx])
                            return
                        }
                    }

                    // Case 2: new file upload (native File object from browser)
                    let fileURL = ''
                    try {
                        fileURL = file instanceof File ? URL.createObjectURL(file) : (file.url || file.FileURL || '')
                    } catch {
                        fileURL = file.url || file.FileURL || ''
                    }

                    const newMedia = {
                        MediaId: Date.now() + idx,
                        FileURL: fileURL,
                        SignedURL: fileURL,
                        FileKey: file.name || `file_${idx}`,
                        MimeType: file.type || 'image/jpeg',
                        Caption: file.Caption || '',
                        Comment: file.Comment || '',
                    }
                    targetQ.Answers.MediaResponse.push(newMedia)
                    newMediaList.push(newMedia)
                })

                setData(newData)
            }
        }
        return newMediaList
    }

    if (templateLoading || !data) {
        return (
            <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
                <LoaderBar content="Loading inspection preview..." />
            </div>
        )
    }

    if (publicError) {
        return <div className="error-message">{publicError}</div>
    }

    return (
        <div>
            {isLanguageFeatureEnabled && (
                <LanguageSelectorContainer>
                    <span className="language-label">Language:</span>
                    <Dropdown
                        className="language-dropdown"
                        selection
                        options={languageOptions}
                        value={selectedLanguage}
                        onChange={(e, { value }) => setSelectedLanguage(value)}
                        disabled={translating || !data}
                        loading={translating}
                    />
                </LanguageSelectorContainer>
            )}
            <FlexColumn className="mb-4 col-12">
                <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ margin: 0 }}>Title: </label>
                </FlexRow>
                <FlexRow>
                    <Input
                        className="col-12"
                        value={displayData?.AuditName || data?.AuditName || data?.TemplateName || 'Inspection Preview'}
                        disabled
                    />
                </FlexRow>
            </FlexColumn>

            {translating && <LoaderBar content="Translating..." />}
            {isSpecialFinishMatTemplate &&
                finishMatRenderGroups.map((group, groupIndex) => {
                    const isPortalLayout =
                        group.layout === FINISH_MAT_SECTION_LAYOUT.PORTAL
                    const isHorizontalLayout =
                        group.layout === FINISH_MAT_SECTION_LAYOUT.HORIZONTAL
                    const isLastFinishMatGroup =
                        groupIndex === finishMatGroupCount - 1
                    return (
                        <InspectionSectionList
                            key={`finish-mat-${group.layout}-${group.sections
                                .map(section => section.SectionId)
                                .join('_')}`}
                            list={group.sections}
                            loading={templateLoading}
                            submit={() => {}}
                            onAnswerChanged={onAnswerChanged}
                            errors={{}}
                            submitError={publicError}
                            addMedia={addMedia}
                            addNotes={saveNote}
                            inspectionTemplateId={data?.Id}
                            reasonPresetSetting={data?.ReasonPresetSetting}
                            onBack={() => navigate(-1)}
                            printByTemplate={!!data?.PrintByTemplate}
                            resetBySection={!!data?.ResetBySection}
                            showSectionDropdown={showSectionDropdown}
                            isShowHorizontal={isHorizontalLayout}
                            portalCardSectionIds={
                                isPortalLayout
                                    ? [FINISH_MAT_GENERAL_INFO_SECTION_ID]
                                    : undefined
                            }
                            portalCardSectionNames={
                                isPortalLayout
                                    ? [FINISH_MAT_GENERAL_INFO_SECTION_NAME]
                                    : undefined
                            }
                            totalScore={
                                isLastFinishMatGroup ? totalScore : undefined
                            }
                            totalQuestions={
                                isLastFinishMatGroup
                                    ? templateTotalQuestions
                                    : undefined
                            }
                            publicScreen={false}
                            isReadOnly={false}
                            showActionButtons={false}
                            showValidation={false}
                            isReopened={false}
                            templateList={[]}
                            validateForComplete={() => true}
                            hideHorizontalAddNew={isHorizontalLayout}
                            hideFooterActions={
                                isPortalLayout || !isLastFinishMatGroup
                            }
                            hideSectionToolbar
                            getCopyButtonLabel={getPreviewSectionCopyButtonLabel}
                        />
                    )
                })}
            {!isSpecialFinishMatTemplate && (
            <InspectionSectionList
                list={sectionsForRenderForPreview}
                loading={templateLoading}
                submit={() => { }}
                onAnswerChanged={onAnswerChanged}
                errors={{}}
                submitError={publicError}
                addMedia={addMedia}
                addNotes={saveNote}
                inspectionTemplateId={data?.Id}
                reasonPresetSetting={data?.ReasonPresetSetting}
                onBack={() => navigate(-1)}
                printByTemplate={!!data?.PrintByTemplate}
                resetBySection={!!data?.ResetBySection}
                showSectionDropdown={showSectionDropdown}
                isShowHorizontal={!!data?.IsShowHorizontal}
                totalScore={totalScore}
                publicScreen={false}
                isReadOnly={false}
                showActionButtons={false}
                showValidation={false}
                isReopened={false}
                templateList={[]}
                validateForComplete={() => true}
                getCopyButtonLabel={getPreviewSectionCopyButtonLabel}
            />
            )}
        </div>
    )
}

export default InspectionPreviewScreen